import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

function EliForm() {
  const [taskName, setTaskName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('normal');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('add');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const [editingTask, setEditingTask] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDue, setEditDue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editSaving, setEditSaving] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('eli_tasks').select('*').order('created_at', { ascending: false });
    setTasks(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const uploadFiles = async () => {
    const uploaded = [];
    for (const file of files) {
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${file.name.split('.').pop()}`;
      const { error } = await supabase.storage.from('attachments').upload(`eli/${fileName}`, file);
      if (!error) {
        const { data: { publicUrl } } = supabase.storage.from('attachments').getPublicUrl(`eli/${fileName}`);
        uploaded.push({ name: file.name, url: publicUrl, type: file.type, size: file.size });
      }
    }
    return uploaded;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskName.trim()) { setError('Please enter a task name'); return; }
    setSubmitting(true); setError('');
    let attachments = [];
    if (files.length > 0) { setUploading(true); attachments = await uploadFiles(); setUploading(false); }
    const { error: err } = await supabase.from('eli_tasks').insert({ task_name: taskName.trim(), due_date: dueDate || null, notes: notes.trim() || null, priority, attachments, status: 'todo', completed: false });
    if (err) { setError('Failed to submit'); setSubmitting(false); return; }
    setSubmitted(true); setSubmitting(false); loadTasks();
  };

  const toggleComplete = async (id) => {
    const task = tasks.find(t => t.id === id);
    const newCompleted = !task.completed;
    await supabase.from('eli_tasks').update({ completed: newCompleted }).eq('id', id);
    setTasks(tasks.map(t => t.id === id ? { ...t, completed: newCompleted } : t));
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setEditName(task.task_name);
    setEditDue(task.due_date || '');
    setEditNotes(task.notes || '');
    setEditPriority(task.priority || 'normal');
  };

  const saveEdit = async () => {
    if (!editingTask || !editName.trim()) return;
    setEditSaving(true);
    const { error: err } = await supabase.from('eli_tasks').update({
      task_name: editName.trim(),
      due_date: editDue || null,
      notes: editNotes.trim() || null,
      priority: editPriority,
    }).eq('id', editingTask.id);
    if (!err) {
      setTasks(tasks.map(t => t.id === editingTask.id ? { ...t, task_name: editName.trim(), due_date: editDue || null, notes: editNotes.trim() || null, priority: editPriority } : t));
      setEditingTask(null);
    }
    setEditSaving(false);
  };

  const reset = () => { setTaskName(''); setDueDate(''); setNotes(''); setPriority('normal'); setFiles([]); setSubmitted(false); };
  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  const formatSize = (b) => b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';
  const getIcon = (t) => t?.startsWith('image/') ? '🖼️' : t?.includes('pdf') ? '📄' : '📎';
  const renderLinks = (text) => text?.split(/(https?:\/\/[^\s]+)/g).map((p, i) => p.match(/https?:\/\//) ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: '#8b5cf6' }}>{p}</a> : p);

  const pending = tasks.filter(t => !t.completed);
  const completed = tasks.filter(t => t.completed);
  const priorities = [
    { id: 'normal', label: '😊 Normal', color: '#64748b' },
    { id: 'medium', label: '🟡 Medium', color: '#f59e0b' },
    { id: 'high', label: '🔴 High', color: '#ef4444' },
  ];

  const inputStyle = {
    width: '100%',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#0f172a',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const TaskItem = ({ task }) => (
    <div style={{ background: task.completed ? '#f0fdf4' : 'white', borderRadius: 10, padding: 14, marginBottom: 10, border: '1px solid #e2e8f0', borderLeft: task.priority === 'high' ? '3px solid #ef4444' : task.priority === 'medium' ? '3px solid #f59e0b' : '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div onClick={() => toggleComplete(task.id)} style={{ width: 22, height: 22, borderRadius: 6, border: task.completed ? 'none' : '2px solid #cbd5e1', background: task.completed ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'white', fontSize: 12, marginTop: 1 }}>{task.completed && '✓'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {task.priority !== 'normal' && <span>{task.priority === 'high' ? '🔴' : '🟡'}</span>}
            <span style={{ color: task.completed ? '#10b981' : '#0f172a', fontSize: 14, fontWeight: 600, textDecoration: task.completed ? 'line-through' : 'none', opacity: task.completed ? 0.7 : 1 }}>{task.task_name}</span>
          </div>
          {task.notes && <div style={{ color: '#64748b', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{renderLinks(task.notes)}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {task.due_date && <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 10, background: '#fffbeb', color: '#f59e0b', fontWeight: 600 }}>Due: {formatDate(task.due_date)}</span>}
            {task.attachments?.length > 0 && <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 10, background: '#eff6ff', color: '#3b82f6', fontWeight: 600 }}>📎 {task.attachments.length}</span>}
          </div>
          {task.attachments?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {task.attachments.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f5f3ff', borderRadius: 8, padding: '8px 12px', marginTop: 6, textDecoration: 'none' }}>
                  <span>{getIcon(f.type)}</span>
                  <span style={{ color: '#8b5cf6', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ color: '#94a3b8', fontSize: 10 }}>{formatSize(f.size)}</span>
                </a>
              ))}
            </div>
          )}
        </div>
        {!task.completed && (
          <button onClick={() => openEdit(task)} style={{ background: '#f5f3ff', border: 'none', color: '#8b5cf6', cursor: 'pointer', fontSize: 12, padding: '5px 10px', borderRadius: 6, flexShrink: 0, fontWeight: 600 }}>✏️ Edit</button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)', padding: 20, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input[type="date"] { color-scheme: light; }
        input, button, textarea { font-family: inherit; }
        .eli-input:focus { border-color: #8b5cf6 !important; box-shadow: 0 0 0 3px rgba(139,92,246,0.1); }
      `}</style>

      {/* Edit Modal */}
      {editingTask && (
        <div onClick={() => setEditingTask(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 16, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Edit Task</h2>
              <button onClick={() => setEditingTask(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 24, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name *</label>
                <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} className="eli-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {priorities.map(p => (
                    <button key={p.id} type="button" onClick={() => setEditPriority(p.id)} style={{ flex: 1, padding: '10px 8px', border: editPriority === p.id ? `2px solid ${p.color}` : '2px solid #e2e8f0', background: editPriority === p.id ? `${p.color}12` : 'white', color: editPriority === p.id ? p.color : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}>{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</label>
                <input type="date" value={editDue} onChange={e => setEditDue(e.target.value)} style={inputStyle} className="eli-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} className="eli-input" />
              </div>
              <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
                <button onClick={saveEdit} disabled={editSaving} style={{ flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: 'white', fontWeight: 700, cursor: editSaving ? 'not-allowed' : 'pointer', opacity: editSaving ? 0.7 : 1, fontSize: 14 }}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
                <button onClick={() => setEditingTask(null)} style={{ padding: '12px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28, paddingTop: 20 }}>
          <div style={{ width: 70, height: 70, borderRadius: 18, background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 32, boxShadow: '0 8px 24px rgba(139,92,246,0.3)' }}>📋</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }}>Hey Eli!</h1>
          <p style={{ color: '#64748b', margin: '8px 0 0', fontSize: 14 }}>Submit tasks for Tanner</p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
          {['add', 'pending', 'completed'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '10px 18px', border: 'none', background: 'transparent', color: activeTab === tab ? '#8b5cf6' : '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderBottom: activeTab === tab ? '2px solid #8b5cf6' : '2px solid transparent', marginBottom: -2 }}>
              {tab === 'add' ? '➕ Add' : tab === 'pending' ? `⏳ Pending${pending.length > 0 ? ` (${pending.length})` : ''}` : '✅ Done'}
            </button>
          ))}
        </div>

        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 20, padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          {activeTab === 'add' && (submitted ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 36 }}>✓</div>
              <h2 style={{ color: '#10b981', margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>Task Submitted!</h2>
              <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>Tanner will be notified.</p>
              <button onClick={reset} style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: 'white', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(139,92,246,0.3)' }}>Add Another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {error && <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, border: '1px solid #fecaca' }}>{error}</div>}
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name *</label>
                <input value={taskName} onChange={e => setTaskName(e.target.value)} placeholder="What needs to be done?" style={inputStyle} className="eli-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Priority</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {priorities.map(p => (
                    <button key={p.id} type="button" onClick={() => setPriority(p.id)} style={{ flex: 1, padding: '10px 8px', border: priority === p.id ? `2px solid ${p.color}` : '2px solid #e2e8f0', background: priority === p.id ? `${p.color}12` : 'white', color: priority === p.id ? p.color : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 8 }}>{p.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Date</label>
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inputStyle} className="eli-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Details or links..." rows={3} style={{ ...inputStyle, resize: 'vertical' }} className="eli-input" />
              </div>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Attachments</label>
                <div
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={e => { e.preventDefault(); setIsDragging(false); setFiles(f => [...f, ...Array.from(e.dataTransfer.files)]); }}
                  style={{ border: isDragging ? '2px dashed #8b5cf6' : '2px dashed #cbd5e1', borderRadius: 10, padding: 20, textAlign: 'center', background: isDragging ? '#f5f3ff' : '#fafafa', transition: 'all 0.15s' }}
                >
                  <input type="file" multiple onChange={e => setFiles(f => [...f, ...Array.from(e.target.files)])} style={{ display: 'none' }} id="file-upload" />
                  <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>{isDragging ? '📥' : '📎'}</div>
                    <div style={{ color: '#8b5cf6', fontSize: 13, fontWeight: 600 }}>Drag & drop or tap to attach</div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 3 }}>Images, PDFs, and more</div>
                  </label>
                </div>
                {files.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f5f3ff', borderRadius: 8, padding: '8px 12px', marginBottom: 6, border: '1px solid #ede9fe' }}>
                        <span>{getIcon(f.type)}</span>
                        <span style={{ flex: 1, color: '#0f172a', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>{formatSize(f.size)}</span>
                        <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" disabled={submitting} style={{ background: submitting ? '#a78bfa' : 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: 'white', border: 'none', borderRadius: 12, padding: 15, fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: submitting ? 'none' : '0 4px 12px rgba(139,92,246,0.3)', marginTop: 4 }}>
                {uploading ? 'Uploading files...' : submitting ? 'Submitting...' : 'Submit Task'}
              </button>
            </form>
          ))}

          {activeTab === 'pending' && (
            <div>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 16px', textAlign: 'center' }}>Tap checkbox to mark complete · ✏️ to edit</p>
              {loading ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Loading...</p>
              ) : pending.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                  <p style={{ color: '#8b5cf6', fontSize: 15, fontWeight: 600 }}>All caught up!</p>
                </div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>{pending.map(t => <TaskItem key={t.id} task={t} />)}</div>
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div>
              {loading ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>Loading...</p>
              ) : completed.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
                  <p style={{ color: '#8b5cf6', fontSize: 15, fontWeight: 600 }}>No completed tasks yet</p>
                </div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>{completed.map(t => <TaskItem key={t.id} task={t} />)}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EliForm;
